import { getSupabaseClient } from '../supabase';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  created_at: string;
  onboarding_step: string;
  onboarding_completed_at: string | null;
  is_suspended: boolean;
  role: 'user' | 'admin';
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalLeads: number;
  leadsThisWeek: number;
  emailsSent: number;
}

export async function getAdminUsers(): Promise<AdminUser[]> {
  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .select('id, user_id, full_name, first_name, last_name, company_name, created_at, onboarding_step, onboarding_completed_at, is_suspended, role')
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Get user emails from auth (would need service role key in production)
  // For now, return profiles with user_id as email placeholder
  return ((data ?? []) as any[]).map((p) => ({
    id: p.user_id,
    email: p.user_id, // Will be replaced with actual email lookup
    full_name: p.full_name,
    first_name: p.first_name,
    last_name: p.last_name,
    company_name: p.company_name,
    created_at: p.created_at,
    onboarding_step: p.onboarding_step,
    onboarding_completed_at: p.onboarding_completed_at,
    is_suspended: p.is_suspended ?? false,
    role: p.role ?? 'user',
  }));
}

/**
 * Suspend or unsuspend a user
 */
export async function suspendUser(userId: string, suspend: boolean): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('profiles')
    .update({ is_suspended: suspend, updated_at: new Date().toISOString() })
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Update a user's role
 */
export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('profiles')
    .update({ role, updated_at: new Date().toISOString() })
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Delete a user and all their data
 */
export async function deleteUser(userId: string): Promise<void> {
  const supabase = getSupabaseClient();

  // Delete in order due to foreign key constraints
  // 1. Delete email drafts
  await supabase.from('email_drafts').delete().eq('user_id', userId);

  // 2. Delete leads
  await supabase.from('leads').delete().eq('user_id', userId);

  // 3. Delete send logs
  await supabase.from('send_logs').delete().eq('user_id', userId);

  // 4. Delete reply logs
  await supabase.from('reply_logs').delete().eq('user_id', userId);

  // 5. Delete notifications
  await supabase.from('notifications').delete().eq('user_id', userId);

  // 6. Delete do_not_contact entries
  await supabase.from('do_not_contact').delete().eq('user_id', userId);

  // 7. Delete subscription
  await supabase.from('subscriptions').delete().eq('user_id', userId);

  // 8. Delete profile
  const { error } = await supabase.from('profiles').delete().eq('user_id', userId);

  if (error) throw error;

  // Note: Firebase Auth user deletion would need to be done via Firebase Admin SDK
  // in a secure backend function. For MVP, we just soft-delete data.
}

export interface AgentLog {
  id: string;
  user_id: string;
  agent_type: 'scout' | 'scribe';
  action: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  lead_id: string | null;
  input_data: Record<string, unknown> | null;
  output_data: Record<string, unknown> | null;
  error_message: string | null;
  tokens_used: number | null;
  duration_ms: number | null;
  created_at: string;
  completed_at: string | null;
  // Joined data
  user_name?: string;
  lead_name?: string;
}

export interface AgentStats {
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  scoutRuns: number;
  scribeRuns: number;
  totalTokens: number;
  avgDurationMs: number;
}

/**
 * Get agent logs with pagination
 */
export async function getAgentLogs(options: {
  limit?: number;
  offset?: number;
  agentType?: 'scout' | 'scribe';
  status?: 'pending' | 'running' | 'completed' | 'failed';
} = {}): Promise<AgentLog[]> {
  const { limit = 50, offset = 0, agentType, status } = options;

  let query = getSupabaseClient()
    .from('agent_logs')
    .select(`
      *,
      profiles:user_id (full_name, first_name, last_name)
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (agentType) {
    query = query.eq('agent_type', agentType);
  }

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) {
    // Table might not have data yet
    if (error.code === '42P01') return [];
    throw error;
  }

  return ((data ?? []) as any[]).map((log) => ({
    ...log,
    user_name: log.profiles?.full_name || log.profiles?.first_name || 'Unknown',
  }));
}

/**
 * Get agent statistics
 */
export async function getAgentStats(): Promise<AgentStats> {
  const supabase = getSupabaseClient();

  // Get total runs
  const { count: totalRuns } = await supabase
    .from('agent_logs')
    .select('*', { count: 'exact', head: true });

  // Get successful runs
  const { count: successfulRuns } = await supabase
    .from('agent_logs')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'completed');

  // Get failed runs
  const { count: failedRuns } = await supabase
    .from('agent_logs')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'failed');

  // Get scout runs
  const { count: scoutRuns } = await supabase
    .from('agent_logs')
    .select('*', { count: 'exact', head: true })
    .eq('agent_type', 'scout');

  // Get scribe runs
  const { count: scribeRuns } = await supabase
    .from('agent_logs')
    .select('*', { count: 'exact', head: true })
    .eq('agent_type', 'scribe');

  // Get token and duration stats
  const { data: statsData } = await supabase
    .from('agent_logs')
    .select('tokens_used, duration_ms')
    .not('tokens_used', 'is', null);

  let totalTokens = 0;
  let totalDuration = 0;
  let durationCount = 0;

  (statsData ?? []).forEach((log: { tokens_used: number | null; duration_ms: number | null }) => {
    if (log.tokens_used) totalTokens += log.tokens_used;
    if (log.duration_ms) {
      totalDuration += log.duration_ms;
      durationCount++;
    }
  });

  return {
    totalRuns: totalRuns ?? 0,
    successfulRuns: successfulRuns ?? 0,
    failedRuns: failedRuns ?? 0,
    scoutRuns: scoutRuns ?? 0,
    scribeRuns: scribeRuns ?? 0,
    totalTokens,
    avgDurationMs: durationCount > 0 ? Math.round(totalDuration / durationCount) : 0,
  };
}

export async function getAdminStats(): Promise<AdminStats> {
  const supabase = getSupabaseClient();

  // Get user count
  const { count: totalUsers } = await supabase
    .from('profiles')
    .select('*', { count: 'exact', head: true });

  // Get users who completed onboarding
  const { count: activeUsers } = await supabase
    .from('profiles')
    .select('*', { count: 'exact', head: true })
    .not('onboarding_completed_at', 'is', null);

  // Get total leads
  const { count: totalLeads } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true });

  // Get leads from this week
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const { count: leadsThisWeek } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', oneWeekAgo.toISOString());

  // Get emails sent (leads with contacted status)
  const { count: emailsSent } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'contacted');

  return {
    totalUsers: totalUsers ?? 0,
    activeUsers: activeUsers ?? 0,
    totalLeads: totalLeads ?? 0,
    leadsThisWeek: leadsThisWeek ?? 0,
    emailsSent: emailsSent ?? 0,
  };
}

export interface InviteCode {
  id: string;
  code: string;
  max_uses: number;
  current_uses: number;
  expires_at: string | null;
  created_at: string;
  is_active: boolean;
}

export async function getInviteCodes(): Promise<InviteCode[]> {
  const { data, error } = await getSupabaseClient()
    .from('invite_codes')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    // Table might not exist yet
    if (error.code === '42P01') return [];
    throw error;
  }

  return (data ?? []) as InviteCode[];
}

export async function createInviteCode(
  code: string,
  maxUses: number = 100,
  expiresAt?: string
): Promise<InviteCode> {
  const { data, error } = await getSupabaseClient()
    .from('invite_codes')
    .insert({
      code: code.toUpperCase(),
      max_uses: maxUses,
      current_uses: 0,
      expires_at: expiresAt || null,
      is_active: true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as InviteCode;
}

export async function validateInviteCode(code: string): Promise<boolean> {
  const { data, error } = await getSupabaseClient()
    .from('invite_codes')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('is_active', true)
    .single();

  if (error || !data) return false;

  const invite = data as InviteCode;

  // Check if expired
  if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
    return false;
  }

  // Check if max uses reached
  if (invite.current_uses >= invite.max_uses) {
    return false;
  }

  return true;
}

export async function useInviteCode(code: string): Promise<boolean> {
  const supabase = getSupabaseClient();

  // First validate
  const isValid = await validateInviteCode(code);
  if (!isValid) return false;

  // Increment usage
  const { error } = await supabase.rpc('increment_invite_code_usage', {
    invite_code: code.toUpperCase(),
  });

  // If RPC doesn't exist, do it manually
  if (error) {
    const { error: updateError } = await supabase
      .from('invite_codes')
      .update({ current_uses: supabase.rpc('current_uses + 1') })
      .eq('code', code.toUpperCase());

    if (updateError) {
      console.error('Failed to increment invite code usage:', updateError);
    }
  }

  return true;
}
